import { Component, OnInit, OnDestroy, input, model } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  UntypedFormArray,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { IWarehouse } from 'src/app/@interfaces/warehouse.interface';
import { InventoryService } from 'src/app/@pages/inventory/inventory.service';
import { WarehouseService } from 'src/app/@pages/warehouse/warehouse.service';
import { ValidatorsService } from 'src/app/@shared/validator/validators.service';
import { ITaxes } from 'src/app/@interfaces/taxes.interface';
import { ITypes } from 'src/app/@interfaces/types.interface';
import { IUnits } from 'src/app/@interfaces/units.interface';
import { BillyItems } from '../../../../../@interfaces/Billy/billy.interface';
import { IGrupoArt } from 'src/app/@interfaces/grupoart.interface';
import { BillyItemService } from './billyItems.service';
import { NgxSpinnerService, NgxSpinnerModule } from 'ngx-spinner';
import { Subject } from 'rxjs';
import {
  takeUntil,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  startWith,
  map,
} from 'rxjs/operators';
import { of } from 'rxjs';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { IArticles } from 'src/app/@interfaces/article.interface';
import { CookieAuthService } from 'src/app/@shared/storage-variables/cookie-auth.service';
import { Observable } from 'rxjs';
import { IStorageCompany } from 'src/app/@interfaces/company.interface';
import { AlertsService } from '../../../services/alerts/alerts.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { IAlternativeCodeInput } from 'src/app/@interfaces/alternative-code.interface';
import { AlternativeCodeDialogComponent } from '../../alternative-code-dialog/alternative-code-dialog.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { AsyncPipe } from '@angular/common';
import { STRING_CONSTANTS } from '@shared-constants';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DialogComponent } from '../../dialog.component';
import { CompoundArticlesService } from 'src/app/@pages/inventory/components/compound-articles/services/compound-articles.service';
import {
  CompoundArticleCreationInput,
  CompoundArticleCreationResponse,
} from '@shared-models';
import { Router } from '@angular/router';
import { ProviderService } from 'src/app/@pages/provider/provider.service';
import { UnitConversionService } from 'src/app/@shared/services/unit-conversion.service';

@Component({
  selector: 'app-register-article',
  templateUrl: './register-article.component.html',
  styleUrls: ['./register-article.component.css'],
  imports: [
    MatDialogModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatInputModule,
    MatChipsModule,
    MatIconModule,
    MatButtonModule,
    NgxSpinnerModule,
    TranslatePipe,
    MatCheckboxModule,
    MatSelectModule,
    MatTooltipModule,
    FormsModule,
    AsyncPipe,
  ],
})
export class RegisterArticleComponent implements OnInit, OnDestroy {
  isCompoundArticle = input(false);

  warehouseList: Array<IWarehouse> = [];
  taxesList: Array<ITaxes> = [];
  typesList: Array<ITypes> = [];
  unitsList: Array<IUnits> = [];
  grupoartList: Array<IGrupoArt> = [];
  articleForm: UntypedFormGroup = this.fb.group({
    code: ['', [Validators.required, Validators.pattern(/^\S*$/)]],
    description: ['', Validators.required],
    presentation: [
      '',
      [
        Validators.required,
        Validators.pattern(this.validatorS.quantityPattern),
      ],
    ],
    price: [
      '',
      [
        Validators.required,
        Validators.pattern(this.validatorS.quantityPattern),
      ],
    ],
    group: ['', Validators.required],
    unit: [''],
    tax: ['', Validators.required],
    type: ['', Validators.required],
    unitBilly: ['', Validators.required],
    packagingUnit: [''],
    packagingFactor1: [1],
    packagingFactor2: [1],
  });
  unsubscribe$ = new Subject<void>();
  companyId = '';
  companyObject = {} as IStorageCompany;
  billyItems = {} as BillyItems;

  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  alternativeCodesList: IAlternativeCodeInput[] = [];
  providersList: any[] = [];
  isPackagingFactorLocked = false; // Controls if packaging factor is locked due to standard conversion

  readonly produceCompoundArticleCheck = model(false);

  // Compound article specific properties
  filteredArticles: Observable<IArticles[]>[] = [];
  availableArticles: IArticles[] = [];

  get componentsFormArray() {
    const control = this.articleForm.get('components');
    return control ? (control as UntypedFormArray) : this.fb.array([]);
  }

  get packagingFactorTotal(): number {
    // Use getRawValue to access disabled fields (when factor is locked)
    const rawValues = this.articleForm.getRawValue();
    const factor1 = Number(rawValues.packagingFactor1) || 1;
    const factor2 = Number(rawValues.packagingFactor2) || 1;
    return factor1 * factor2;
  }

  constructor(
    private fb: UntypedFormBuilder,
    private validatorS: ValidatorsService,
    private warehouseS: WarehouseService,
    private inventoryS: InventoryService,
    private dialog: MatDialog,
    private billyItemsService: BillyItemService,
    private spinnerService: NgxSpinnerService,
    private alertsService: AlertsService,
    private cookieAuthService: CookieAuthService,
    private compoundArticlesService: CompoundArticlesService,
    private translate: TranslateService,
    private router: Router,
    private providerService: ProviderService,
    private unitConversionService: UnitConversionService,
  ) {}

  ngOnInit(): void {
    this.getAuthValues();

    // Initialize form based on current article type
    this.updateFormValidators();
    this.warehouseS
      .listWarehouses()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        this.warehouseList = result;
      });

    this.inventoryS
      .listTaxes()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        this.taxesList = result;
      });

    this.inventoryS
      .listTypes()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        this.typesList = result;
      });

    this.inventoryS
      .listUnits()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        this.unitsList = result;
      });

    // Load providers list for alternative codes
    this.loadProviders();

    this.inventoryS
      .listGrupoArt()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        this.grupoartList = result;
      });

    // Listen to packaging unit changes
    this.articleForm
      .get('packagingUnit')
      ?.valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.onPackagingUnitChange();
      });
  }

  getAuthValues() {
    this.companyObject = this.cookieAuthService.getCompanyObject!;
    this.companyId = this.companyObject!.Id_company!.toString();
  }

  updateFormValidators() {
    const isCompound = this.isCompoundArticle();

    // Fields that are only required for simple articles
    const simpleArticleFields = [
      'quantity',
      'warehouse',
      'quantityInitial',
      'valueInitial',
    ];

    if (isCompound) {
      // For compound articles: remove simple article fields and add compound fields

      // Remove simple article fields
      simpleArticleFields.forEach((fieldName) => {
        if (this.articleForm.get(fieldName)) {
          this.articleForm.removeControl(fieldName);
        }
      });

      // Add compound article specific controls if they don't exist
      if (!this.articleForm.get('recipeQuantity')) {
        this.articleForm.addControl(
          'recipeQuantity',
          this.fb.control('', [
            Validators.required,
            Validators.pattern(this.validatorS.quantityPattern),
            Validators.min(0.001),
          ]),
        );
      }

      if (!this.articleForm.get('components')) {
        this.articleForm.addControl('components', this.fb.array([]));
      }

      // Set up components validation and initialize with one component
      this.componentsFormArray.setValidators(this.minLengthArray(1));

      if (this.componentsFormArray.length === 0) {
        this.addSubArticle();
      }
    } else {
      // For simple articles: remove compound fields and add simple fields

      // Remove compound article specific controls
      if (this.articleForm.get('recipeQuantity')) {
        this.articleForm.removeControl('recipeQuantity');
      }

      if (this.articleForm.get('components')) {
        // Clear all components first
        while (this.componentsFormArray.length !== 0) {
          this.componentsFormArray.removeAt(0);
        }
        this.filteredArticles = [];
        this.articleForm.removeControl('components');
      }

      // Add simple article fields if they don't exist
      simpleArticleFields.forEach((fieldName) => {
        const validators = [Validators.required];

        if (fieldName !== 'warehouse') {
          validators.push(Validators.pattern(this.validatorS.quantityPattern));
        }

        if (!this.articleForm.get(fieldName)) {
          this.articleForm.addControl(
            fieldName,
            this.fb.control('', validators),
          );
        } else {
          // Re-add validators if control exists
          const control = this.articleForm.get(fieldName);
          control?.setValidators(validators);
          control?.updateValueAndValidity();
        }
      });
    }
  }

  // Custom validator for FormArray minimum length
  private minLengthArray(min: number) {
    return (control: any) => {
      const array = control as UntypedFormArray;
      return array.length < min
        ? { minLengthArray: { min, actual: array.length } }
        : null;
    };
  }

  // FormArray management methods
  createComponentFormGroup(): UntypedFormGroup {
    return this.fb.group({
      article: ['', Validators.required],
      articleSearch: [''], // For autocomplete display
      quantity: [
        '',
        [
          Validators.required,
          Validators.pattern(this.validatorS.quantityPattern),
          Validators.min(0.001),
        ],
      ],
      unit: [''], // Unit of measure for the component
    });
  }

  addSubArticle(): void {
    if (!this.isCompoundArticle()) return;

    const componentGroup = this.createComponentFormGroup();
    this.componentsFormArray.push(componentGroup);

    // Initialize filtered articles for this new component
    const index = this.componentsFormArray.length - 1;
    this.initializeArticleSearch(index);
  }

  removeSubArticle(index: number): void {
    if (!this.isCompoundArticle()) return;

    if (this.componentsFormArray.length > 1) {
      this.componentsFormArray.removeAt(index);
      this.filteredArticles.splice(index, 1);
    } else {
      this.alertsService.showWarningAlert(
        this.translate.instant(
          'inventory.compoundArticles.modals.register.subArticles.removeWarning',
        ),
      );
    }
  }

  initializeArticleSearch(index: number): void {
    const componentGroup = this.componentsFormArray.at(index);
    const searchControl = componentGroup.get('articleSearch');

    if (searchControl) {
      this.filteredArticles[index] = searchControl.valueChanges.pipe(
        startWith(''),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((searchTerm) => this.performArticleSearch(searchTerm || '')),
        takeUntil(this.unsubscribe$),
      );
    }
  }

  private performArticleSearch(searchTerm: string): Observable<IArticles[]> {
    const warehouseId = null;

    if (!searchTerm || searchTerm.length < 2) {
      return of([]);
    }

    return this.inventoryS
      .searchArticle(searchTerm, warehouseId, null, null)
      .pipe(
        map((result) => result.articles || []),
        takeUntil(this.unsubscribe$),
      );
  }

  onArticleSelected(selectedArticle: IArticles, index: number): void {
    const componentGroup = this.componentsFormArray.at(index);
    componentGroup.get('article')?.setValue(selectedArticle);
    componentGroup
      .get('articleSearch')
      ?.setValue(`${selectedArticle.descripcion} - ${selectedArticle.codigo}`);

    // Auto-select the article's unit if available
    if (selectedArticle.unit?.id_unit) {
      componentGroup.get('unit')?.setValue(selectedArticle.unit.id_unit);
    } else if (selectedArticle.id_unit) {
      componentGroup.get('unit')?.setValue(selectedArticle.id_unit);
    }
  }

  register() {
    this.articleForm.markAllAsTouched();
    if (this.articleForm.invalid === true) {
      this.alertsService.showErrorAlert(
        this.translate.instant('There are required fields. Please fill them'),
      );
      return;
    } else {
      if (
        this.companyObject.external_electronic_invoicing ===
        STRING_CONSTANTS.TRUE
      ) {
        this.spinnerService.show();
        this.billyItemsService.sendDian(this.articleForm).then((result) => {
          if (result) {
            this.saveOnDataBase();
          }
        });
      } else {
        this.saveOnDataBase();
      }
    }
  }

  saveOnDataBase() {
    if (this.isCompoundArticle()) {
      // Compound articles don't have packaging unit - unit is defined in the recipe
      const compoundArticleToCreate = {
        codigo: this.articleForm.value.code,
        descripcion: this.articleForm.value.description,
        presentacion: String(this.articleForm.value.presentation || ''),
        costo: this.articleForm.value.price,
        cantidad: 0,
        grupo: parseInt(this.articleForm.value.group.id_grupo),
        unidad: '',
        tax: parseInt(this.articleForm.value.tax.id_tax),
        tipo: parseInt(this.articleForm.value.type.id_type),
        unidad2: parseInt(this.articleForm.value.unitBilly.id_unit),
        id_type2: parseInt(
          this.articleForm.value.group.presentations[0].id_type,
        ),
        alternativeCodes: this.cleanAlternativeCodesForBackend(), // Clean nested objects before sending
        compoundArticle: {
          quantity: parseFloat(this.articleForm.value.recipeQuantity),
          is_active: true,
          components: this.componentsFormArray.value.map((component: any) => ({
            id_component_article: parseInt(component.article.id_articulo),
            quantity: parseFloat(component.quantity),
            id_unit: component.unit
              ? parseInt(component.unit)
              : component.article.id_unit,
          })),
        },
      };

      this.compoundArticlesService
        .createCompoundArticle(
          compoundArticleToCreate as unknown as CompoundArticleCreationInput,
        )
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(
          (compoundArticleCreated: CompoundArticleCreationResponse) => {
            this.spinnerService.hide();

            // If compound article was created successfully, show success alert
            this.alertsService
              .showSuccessfullAlert(
                this.translate.instant(
                  'inventory.compoundArticles.modals.register.registerSuccessfully',
                ),
              )
              .then((result) => {
                if (result.isConfirmed) {
                  // If user marks checkbox to produce stock of the compound article,
                  // Open de produceCompundArticleModal to produce new stock of the new compound article
                  if (this.produceCompoundArticleCheck()) {
                    this.dialog.open(DialogComponent, {
                      data: {
                        produceCompoundArticle: true,
                        compoundArticleId:
                          compoundArticleCreated.id_compound_article,
                      },
                    });
                  } else {
                    this.router
                      .navigateByUrl('/table', { skipLocationChange: true })
                      .then(() => {
                        this.router.navigate(['/inventory'], {
                          queryParams: { tab: 'compound' },
                        });
                      });
                    this.dialog.closeAll();
                  }
                }
              });
          },
        );
    } else {
      // Use getRawValue() to get values from disabled fields (when factor is locked)
      const formValues = this.articleForm.getRawValue();

      this.inventoryS
        .createArticle(
          formValues.code,
          formValues.description,
          String(formValues.presentation || ''),
          formValues.price,
          formValues.quantity,
          parseInt(formValues.group.id_grupo),
          '',
          parseInt(formValues.warehouse.id_almacen),
          parseInt(formValues.tax.id_tax),
          parseInt(formValues.type.id_type),
          parseInt(formValues.unitBilly.id_unit),
          parseInt(formValues.group.presentations[0].id_type),
          formValues.quantityInitial,
          formValues.valueInitial,
          formValues.packagingUnit?.id_unit
            ? parseInt(formValues.packagingUnit.id_unit)
            : undefined,
          formValues.packagingUnit?.id_unit
            ? Number(formValues.packagingFactor1 || 1) *
                Number(formValues.packagingFactor2 || 1)
            : undefined,
          formValues.packagingUnit?.id_unit
            ? Number(formValues.packagingFactor1 || 1)
            : undefined,
          formValues.packagingUnit?.id_unit
            ? Number(formValues.packagingFactor2 || 1)
            : undefined,
          this.cleanAlternativeCodesForBackend(), // Clean nested objects before sending
        )
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((result: IArticles) => {
          if (result.descripcion !== 'articleExist') {
            this.spinnerService.hide();

            this.alertsService
              .showSuccessfullAlert('Article registered successfully')
              .then((result) => {
                if (result.isConfirmed) {
                  this.alertsService.refresh('/table', '/inventory');
                }
              });
          } else if (result.descripcion === 'articleExist') {
            this.spinnerService.hide();
            this.alertsService.showWarningAlert('This article already exist');
          } else {
            this.spinnerService.hide();
            this.alertsService.showErrorAlert('Something was wrong');
          }
        });
    }
  }

  closeDialog() {
    this.dialog.closeAll();
  }

  textValid(text: string) {
    return (
      this.articleForm.get(text)?.invalid && this.articleForm.get(text)?.touched
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  // Clean alternative codes for backend (remove nested objects)
  private cleanAlternativeCodesForBackend(): IAlternativeCodeInput[] {
    return this.alternativeCodesList.map((code) => ({
      alternative_code: code.alternative_code,
      id_provider: code.id_provider,
      id_unit: code.id_unit,
      packaging_quantity: code.packaging_quantity,
      units_per_package: code.units_per_package,
      conversion_factor: code.conversion_factor,
      description: code.description,
      // Exclude nested objects (provider, unit) - these are for display only
    }));
  }

  // New methods for structured alternative codes
  loadProviders(): void {
    this.providerService
      .listProvider(null, null)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (result) => {
          // The API returns { count, providers }
          this.providersList = result.providers || [];
        },
        error: (error) => {
          console.error('Error loading providers:', error);
          this.providersList = [];
        },
      });
  }

  async onPackagingUnitChange(): Promise<void> {
    // Get base unit ID from 'unitBilly' field (for simple articles)
    const baseUnit = this.articleForm.get('unitBilly')?.value;
    const baseUnitId = baseUnit?.id_unit;

    // Get packaging unit ID
    const packagingUnit = this.articleForm.get('packagingUnit')?.value;
    const packagingUnitId = packagingUnit?.id_unit;

    // Reset factor locked state
    this.isPackagingFactorLocked = false;

    // If no packaging unit selected, reset factors and unlock
    if (!packagingUnitId) {
      this.articleForm.patchValue({
        packagingFactor1: 1,
        packagingFactor2: 1,
      });
      this.articleForm.get('packagingFactor1')?.enable();
      this.articleForm.get('packagingFactor2')?.enable();
      return;
    }

    // If no base unit selected, allow manual factor
    if (!baseUnitId) {
      this.articleForm.get('packagingFactor1')?.enable();
      this.articleForm.get('packagingFactor2')?.enable();
      return;
    }

    // Check if there's a standard conversion
    try {
      // For packaging, we need to know: how many base units are in one packaging unit?
      // So we ask for conversion FROM packaging TO base (e.g., Kilogram -> Gram = 1000)
      const hasConversion = await this.unitConversionService.hasConversion(
        Number(packagingUnitId), // FROM packaging unit
        Number(baseUnitId), // TO base unit
      );

      if (hasConversion) {
        // Get the conversion factor from database
        // This gives us: how many base units in one packaging unit
        const factor = await this.unitConversionService
          .getConversionFactor(Number(packagingUnitId), Number(baseUnitId))
          .toPromise();

        if (factor && factor > 0) {
          // Lock the factor and apply standard conversion
          this.isPackagingFactorLocked = true;
          this.articleForm.patchValue({
            packagingFactor1: factor,
            packagingFactor2: 1,
          });

          // Disable the factor fields
          this.articleForm.get('packagingFactor1')?.disable();
          this.articleForm.get('packagingFactor2')?.disable();
        }
      } else {
        // No standard conversion, allow custom factor
        this.isPackagingFactorLocked = false;

        // Enable the factor fields for manual input
        this.articleForm.get('packagingFactor1')?.enable();
        this.articleForm.get('packagingFactor2')?.enable();

        // Reset to default if currently 1x1
        const currentFactor1 = this.articleForm.get('packagingFactor1')?.value;
        const currentFactor2 = this.articleForm.get('packagingFactor2')?.value;

        if (
          (!currentFactor1 || currentFactor1 === 1) &&
          (!currentFactor2 || currentFactor2 === 1)
        ) {
          this.articleForm.patchValue({
            packagingFactor1: 1,
            packagingFactor2: 1,
          });
        }
      }
    } catch (error) {
      console.error('Error checking unit conversion:', error);
      // On error, allow custom factor
      this.isPackagingFactorLocked = false;
      this.articleForm.get('packagingFactor1')?.enable();
      this.articleForm.get('packagingFactor2')?.enable();
    }
  }

  openAlternativeCodeDialog(
    code?: IAlternativeCodeInput,
    index?: number,
  ): void {
    const dialogRef = this.dialog.open(AlternativeCodeDialogComponent, {
      width: '600px',
      data: {
        providers: this.providersList,
        units: this.unitsList,
        alternativeCode: code,
        articlePackagingUnit: this.articleForm.value.packagingUnit,
        articleBaseUnit: this.articleForm.value.unitBilly, // Pass base unit for conversion validation
      },
    });

    dialogRef.afterClosed().subscribe((result: IAlternativeCodeInput) => {
      if (result) {
        if (index !== undefined) {
          // Edit existing code
          this.alternativeCodesList[index] = result;
        } else {
          // Add new code
          this.alternativeCodesList.push(result);
        }
      }
    });
  }

  removeAlternativeCode(index: number): void {
    this.alternativeCodesList.splice(index, 1);
  }

  getProviderName(code: IAlternativeCodeInput): string {
    // First try to use nested provider object from backend
    if (code.provider?.nombre) {
      return code.provider.nombre;
    }
    // Fallback to lookup in providersList
    if (!code.id_provider) return '-';
    const provider = this.providersList.find(
      (p) => String(p.id_provider) === String(code.id_provider),
    );
    return provider ? provider.nombre : '-';
  }

  getUnitName(code: IAlternativeCodeInput): string {
    // First try to use nested unit object from backend
    if (code.unit?.description) {
      return `${code.unit.description} (${code.unit.dian_code})`;
    }
    // Fallback to lookup in unitsList
    if (!code.id_unit) return 'Same as article';
    const unit = this.unitsList.find(
      (u) => String(u.id_unit) === String(code.id_unit),
    );
    return unit ? `${unit.description} (${unit.dian_code})` : '-';
  }
}
